'use client';

/**
 * Admin attribution readout + controls (gap H3 admin UI).
 *
 * Shows the last closed period as a dry run (no money moves from this page):
 * total eye time, per-app share and would-pay at a probe budget, plus the
 * budget / pool cap / per-app boost settings, saved through
 * POST /api/admin/attribution (audited setting writes).
 *
 * Access is enforced by the API route (admin or moderator); this page just
 * renders the 403 as a friendly message.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Coins, PieChart, RefreshCw, Save, ShieldAlert } from 'lucide-react';

const NANOCREDITS_PER_USD = 1e9;

interface AppRow {
  appPostId: string;
  eyetimeMs: number;
  effectiveEyetimeMs: number;
  share: number;
  wouldPay: number;
  recipients: Array<{ userId: string; amount: number; role: string; username?: string; generation?: number }>;
}

interface Readout {
  period: {
    periodKey: string;
    totalEyetimeMs: number;
    budget: number;
    wouldPayTotal: number;
    apps: AppRow[];
  };
  budget: number;
  cumulativePaid: number;
  poolCap: number | null;
  targets: Record<string, number>;
  premiumAllocation: boolean;
}

function usd(nanocredits: number): string {
  return `$${(nanocredits / NANOCREDITS_PER_USD).toFixed(2)}`;
}

function eyeTime(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function AttributionTab() {
  const [data, setData] = useState<Readout | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Form state (dollars in the inputs; nanocredits on the wire).
  const [budgetUsd, setBudgetUsd] = useState('');
  const [capUsd, setCapUsd] = useState('');
  const [note, setNote] = useState('');
  const [probeUsd, setProbeUsd] = useState('');
  const [targetDraft, setTargetDraft] = useState<Record<string, string>>({});

  const load = useCallback(async (probeBudgetUsd?: string) => {
    setLoading(true);
    setError(null);
    try {
      const probe =
        probeBudgetUsd && Number.isFinite(Number(probeBudgetUsd))
          ? `?budget=${Math.round(Number(probeBudgetUsd) * NANOCREDITS_PER_USD)}`
          : '';
      const r = await fetch(`/api/admin/attribution${probe}`, { credentials: 'same-origin' });
      if (r.status === 403) {
        setForbidden(true);
        return;
      }
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      const body = (await r.json()) as Readout;
      setData(body);
      setBudgetUsd((body.budget / NANOCREDITS_PER_USD).toString());
      setCapUsd(body.poolCap == null ? '' : (body.poolCap / NANOCREDITS_PER_USD).toString());
      setTargetDraft(
        Object.fromEntries(Object.entries(body.targets ?? {}).map(([k, v]) => [k, String(v)])),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const targets: Record<string, number> = {};
      for (const [appPostId, raw] of Object.entries(targetDraft)) {
        const trimmed = raw.trim();
        if (trimmed === '' || trimmed === '1') continue; // 1 is the default, no need to store
        const n = Number(trimmed);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error(`Boost for ${appPostId.slice(0, 8)} must be a number of 0 or more`);
        }
        targets[appPostId] = n;
      }
      const budget = Number(budgetUsd);
      if (!Number.isFinite(budget) || budget < 0) {
        throw new Error('Budget must be a number of 0 or more');
      }
      const capTrimmed = capUsd.trim();
      const poolCap =
        capTrimmed === '' ? null : Number.isFinite(Number(capTrimmed)) ? Number(capTrimmed) : NaN;
      if (poolCap !== null && (!Number.isFinite(poolCap) || poolCap < 0)) {
        throw new Error('Pool cap must be empty (no cap) or a number of 0 or more');
      }
      const r = await fetch('/api/admin/attribution', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          budget: Math.round(budget * NANOCREDITS_PER_USD),
          poolCap: poolCap === null ? null : Math.round(poolCap * NANOCREDITS_PER_USD),
          targets,
          note: note.trim() || undefined,
        }),
      });
      if (r.status === 403) {
        setForbidden(true);
        return;
      }
      if (!r.ok) {
        const body = (await r.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Save failed (${r.status})`);
      }
      setNotice('Saved.');
      setNote('');
      await load(probeUsd || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [budgetUsd, capUsd, note, targetDraft, probeUsd, load]);

  const summary = useMemo(() => {
    if (!data) return [];
    return [
      { icon: Coins, label: 'Period budget', value: usd(data.budget) },
      { icon: Coins, label: 'Paid out so far', value: usd(data.cumulativePaid) },
      {
        icon: Coins,
        label: 'Pool cap',
        value: data.poolCap == null ? 'No cap' : usd(data.poolCap),
      },
      {
        icon: Clock,
        label: `Eye time (${data.period.periodKey})`,
        value: eyeTime(data.period.totalEyetimeMs),
      },
    ];
  }, [data]);

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Admin access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You need an admin account to view attribution settings.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="flex items-center gap-2">
        <PieChart className="h-5 w-5" />
        <h1 className="text-xl font-semibold">Attribution</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Last closed period as a dry run: how the current pool would split by eye time. Nothing on
        this page pays anyone; payouts run on the settlement schedule.
      </p>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">{notice}</div>
      ) : null}

      {loading && !data ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : null}

      {data ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {summary.map((s) => (
              <div key={s.label} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <s.icon className="h-3.5 w-3.5" />
                  {s.label}
                </div>
                <div className="mt-1 text-lg font-semibold">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">
                Preview a budget (USD)
              </span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={probeUsd}
                onChange={(e) => setProbeUsd(e.target.value)}
                placeholder={(data.budget / NANOCREDITS_PER_USD).toString()}
                className="w-36 rounded-md border border-border bg-background px-2 py-1.5"
              />
            </label>
            <button
              type="button"
              onClick={() => void load(probeUsd || undefined)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="text-sm text-muted-foreground">
              Would pay {usd(data.period.wouldPayTotal)} at a {usd(data.period.budget)} budget.
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">App</th>
                  <th className="px-3 py-2 font-medium">Eye time</th>
                  <th className="px-3 py-2 font-medium">Share</th>
                  <th className="px-3 py-2 font-medium">Would pay</th>
                  <th className="px-3 py-2 font-medium">Recipients</th>
                  <th className="px-3 py-2 font-medium">Boost</th>
                </tr>
              </thead>
              <tbody>
                {data.period.apps.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                      No usage recorded in this period.
                    </td>
                  </tr>
                ) : (
                  data.period.apps.map((a) => (
                    <tr key={a.appPostId} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{a.appPostId.slice(0, 8)}</td>
                      <td className="px-3 py-2">{eyeTime(a.eyetimeMs)}</td>
                      <td className="px-3 py-2">{(a.share * 100).toFixed(1)}%</td>
                      <td className="px-3 py-2">{usd(a.wouldPay)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {a.recipients.length === 0
                          ? 'None'
                          : a.recipients
                              .map((r) =>
                                r.role === 'contributor' && r.username
                                  ? `@${r.username} ${usd(r.amount)}`
                                  : r.role === 'ancestor' && (r.generation ?? 1) > 1
                                    ? `ancestor ${r.generation} ${usd(r.amount)}`
                                    : `${r.role} ${usd(r.amount)}`,
                              )
                              .join(', ')}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          step="0.1"
                          value={targetDraft[a.appPostId] ?? ''}
                          onChange={(e) =>
                            setTargetDraft((d) => ({ ...d, [a.appPostId]: e.target.value }))
                          }
                          placeholder="1"
                          className="w-20 rounded-md border border-border bg-background px-2 py-1"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold">Settings</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Changes are audited. Budget 0 keeps settlement in measurement mode and pays nothing.
              Boost multiplies an app&apos;s eye time when sizing its share; leave it at 1 for the
              normal weight.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">
                  Period budget (USD)
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={budgetUsd}
                  onChange={(e) => setBudgetUsd(e.target.value)}
                  className="w-36 rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">
                  Pool cap (USD, empty for no cap)
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={capUsd}
                  onChange={(e) => setCapUsd(e.target.value)}
                  className="w-44 rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
              <label className="flex-1 text-sm">
                <span className="mb-1 block text-xs text-muted-foreground">Note (optional)</span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why this change"
                  maxLength={500}
                  className="w-full min-w-48 rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
